import React, { createContext, useContext, useState, ReactNode } from 'react'

// FAQ 타입
export interface BexcoFaq {
  id: number
  question: string
  answer: string
  category: string
  createdAt: string
  updatedAt?: string
}

// BEXCO 쿼리 로그 타입
export interface BexcoQueryLog {
  id: number
  query_text: string
  response_text: string
  ip_address?: string
  ip?: string
  query_type?: string
  rag_status: string
  retrieved_context?: string
  building?: string
  floor?: string
  roomNumber?: string
  confidence?: number
  needsClarification?: boolean
  processingTime?: number
  created_at: string
  userId?: string
  clientId?: string
  is_faq?: boolean | number  // FAQ 클릭 여부
  keyword?: string           // 🆕 추출된 키워드
  // 🆕 v10.8: 사용자 유형
  user_type?: string         // default, digital-twin, mobile 등
  // 🆕 v10.3: 검증 결과
  validation_type?: string   // NONE, COMMUNICATION, NO_ANSWER, FORMAT, IRRELEVANT, WRONG
  validation_reason?: string
  validated_at?: string
  validated_by?: string
}

// BEXCO 통계 타입
export interface BexcoStats {
  totalQueries: number
  totalFaqs: number
  averageConfidence: number
  buildingDistribution: Record<string, number>
  queryTypesDistribution: Record<string, number>
  dailyQueries: Array<{ date: string; count: number }>
}

interface BexcoContextType {
  // 자주하는 질문
  faqs: BexcoFaq[]
  setFaqs: (faqs: BexcoFaq[]) => void
  addFaq: (faq: Omit<BexcoFaq, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateFaq: (id: number, faq: Partial<BexcoFaq>) => void
  deleteFaq: (id: number) => void

  // 쿼리 로그
  queryLogs: BexcoQueryLog[]
  setQueryLogs: (logs: BexcoQueryLog[]) => void

  // 통계
  stats: BexcoStats | null
  setStats: (stats: BexcoStats) => void

  // 로딩 상태
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
}

const BexcoContext = createContext<BexcoContextType | undefined>(undefined)

interface BexcoProviderProps {
  children: ReactNode
}

export const BexcoProvider: React.FC<BexcoProviderProps> = ({ children }) => {
  const [faqs, setFaqs] = useState<BexcoFaq[]>([])
  const [queryLogs, setQueryLogs] = useState<BexcoQueryLog[]>([])
  const [stats, setStats] = useState<BexcoStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // 자주하는 질문 관리 함수들
  const addFaq = (faq: Omit<BexcoFaq, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newFaq: BexcoFaq = {
      ...faq,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setFaqs([...faqs, newFaq])
  }

  const updateFaq = (id: number, updatedFaq: Partial<BexcoFaq>) => {
    setFaqs(faqs.map(faq => 
      faq.id === id 
        ? { ...faq, ...updatedFaq, updatedAt: new Date().toISOString() }
        : faq
    ))
  }

  const deleteFaq = (id: number) => {
    setFaqs(faqs.filter(faq => faq.id !== id))
  }

  const value: BexcoContextType = {
    // 자주하는 질문
    faqs,
    setFaqs,
    addFaq,
    updateFaq,
    deleteFaq,

    // 쿼리 로그
    queryLogs,
    setQueryLogs,

    // 통계
    stats,
    setStats,

    // 로딩 상태
    isLoading,
    setIsLoading,
  }

  return (
    <BexcoContext.Provider value={value}>
      {children}
    </BexcoContext.Provider>
  )
}

export const useBexco = (): BexcoContextType => {
  const context = useContext(BexcoContext)
  if (context === undefined) {
    throw new Error('useBexco must be used within a BexcoProvider')
  }
  return context
}






